/**MTNFinance.js — Finance & Green Infrastructure */
import React, { useState, useEffect } from "react";
import { useMTN } from "@/context/MTNContext";
import client from "@/api/client";
import { AlertTriangle, Download, Loader, TrendingUp, Shield } from "lucide-react";
import { toast } from "sonner";
import ExecutiveActionPanel from "@/components/ExecutiveActionPanel";

const fmtU=v=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`;if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`;if(n>=1e3)return`$${(n/1e3).toFixed(0)}K`;return`$${n}`;};
const fmtN=(v,u="")=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e6)return`${(n/1e6).toFixed(1)}M ${u}`.trim();if(n>=1e3)return`${(n/1e3).toFixed(0)}K ${u}`.trim();return`${n.toLocaleString()} ${u}`.trim();};
const TABS=[{id:"finance",label:"Green Finance"},{id:"dfi",label:"DFI Readiness"},{id:"scenarios",label:"Scenarios"},{id:"projects",label:"Project Ranking"}];

export default function MTNFinance() {
  const { selectedPeriod, loading, projects } = useMTN();
  const [activeTab,setActiveTab]=useState("finance");
  const [analysis,setAnalysis]=useState(null);
  const [fetching,setFetching]=useState(false);
  const [downloading,setDownloading]=useState(null);
  const [debtPct,setDebtPct]=useState(55);
  const [equityPct,setEquityPct]=useState(25);
  const [concPct,setConcPct]=useState(20);
  const [tenor,setTenor]=useState(7);

  const fetchFinance = async() => {
    if(!selectedPeriod)return;
    setFetching(true);
    try {
      const r=await client.get(`/mtn/financing/analysis/${selectedPeriod}?debt_pct=${debtPct}&equity_pct=${equityPct}&conc_pct=${concPct}&tenor_yr=${tenor}`);
      setAnalysis(r.data);
    } catch{ toast.error("Finance load failed"); }
    finally { setFetching(false); }
  };

  useEffect(()=>{ fetchFinance(); },[selectedPeriod]);

  const downloadPdf=async(type)=>{
    setDownloading(type);
    try{
      const r=await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mtn/pdf/${type}/${selectedPeriod}?debt_pct=${debtPct}&equity_pct=${equityPct}&conc_pct=${concPct}&tenor_yr=${tenor}`,
        {headers:{Authorization:`Bearer ${localStorage.getItem("decarboniq_token")}`}});
      if(!r.ok)throw new Error();
      const blob=await r.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download=`MTN_${type}_${selectedPeriod}.pdf`;a.click();
      URL.revokeObjectURL(url);toast.success("Downloaded");
    }catch{toast.error("Download failed");}
    finally{setDownloading(null);}
  };

  if(loading)return<div style={{padding:40,color:"#64748b",fontSize:12}}>Loading…</div>;
  const cap=analysis?.capital_stack||{};
  const fin=analysis?.financials||{};
  const sll=analysis?.sll_kpis||[];
  const icma=analysis?.icma_categories||{};
  const projList=projects.list||[];

  return(
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>Finance & Green Infrastructure</h1>
        <p style={{fontSize:11,color:"#475569"}}>Green Bond · SLL · ICMA Framework · DFI readiness · Climate CAPEX · DSCR sensitivity</p>
      </div>

      {/* Capital Stack Config */}
      <div style={{background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:16}}>
        <p style={{fontSize:8,fontWeight:700,textTransform:"uppercase",color:"#475569",letterSpacing:".08em",marginBottom:12}}>Capital Stack Configuration</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:12}}>
          {[["Senior Debt",debtPct,setDebtPct,"#60a5fa"],["Equity",equityPct,setEquityPct,"#22c55e"],["Concessional",concPct,setConcPct,"#a78bfa"]].map(([l,v,s,c])=>(
            <div key={l}>
              <p style={{fontSize:8,color:"#475569",marginBottom:4}}>{l}</p>
              <input type="range" min={0} max={100} value={v} onChange={e=>s(Number(e.target.value))}
                style={{width:"100%",accentColor:c,height:4}} />
              <p style={{fontSize:13,fontWeight:900,color:c,marginTop:2}}>{v}%</p>
            </div>
          ))}
          <div>
            <p style={{fontSize:8,color:"#475569",marginBottom:4}}>Bond Tenor</p>
            <div style={{display:"flex",gap:4}}>
              {[5,7,10,15].map(t=>(
                <button key={t} onClick={()=>setTenor(t)}
                  style={{padding:"3px 8px",borderRadius:4,border:"none",cursor:"pointer",fontSize:9,fontWeight:700,
                    background:tenor===t?"#FFCB00":"#1e293b",color:tenor===t?"#000":"#64748b"}}>{t}yr</button>
              ))}
            </div>
          </div>
        </div>
        <button onClick={fetchFinance} disabled={fetching}
          style={{background:"#FFCB00",color:"#000",border:"none",borderRadius:6,padding:"6px 16px",fontSize:10,fontWeight:700,cursor:"pointer"}}
          data-testid="recalculate-btn">{fetching?"Recalculating…":"Recalculate"}</button>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:6,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,padding:"7px 8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:10,fontWeight:600,
              background:activeTab===t.id?"#FFCB00":"transparent",color:activeTab===t.id?"#000":"#475569"}}
            data-testid={`finance-tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* Green Finance tab */}
      {activeTab==="finance" && analysis && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="capital-stack">
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Capital Stack</p>
            {[["Total CAPEX Pipeline",fmtU(cap.total_capex),"#f1f5f9"],["ICMA-Eligible CAPEX",fmtU(cap.eligible_capex),"#22c55e"],
              ["Indicative Bond Size",fmtU(cap.bond_size),"#60a5fa"],["Concessional (DFI)",fmtU(cap.concessional),"#a78bfa"],["Equity Co-investment",fmtU(cap.equity),"#22c55e"],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:8,borderBottom:"1px solid #0f172a",paddingBottom:6}}>
                <span style={{fontSize:9,color:"#94a3b8"}}>{l}</span>
                <span style={{fontSize:10,fontWeight:700,color:c}}>{v}</span>
              </div>
            ))}
            <div style={{borderTop:"1px solid #1e293b",marginTop:4,paddingTop:10}}>
              <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:6}}>ICMA Categories</p>
              {Object.entries(icma).filter(([,v])=>v>0).map(([k,v])=>(
                <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:9,color:"#64748b"}}>{k}</span>
                  <span style={{fontSize:9,fontWeight:700,color:"#22c55e"}}>{fmtU(v)}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>DSCR Sensitivity — {tenor}yr</p>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{borderBottom:"1px solid #1e293b"}}>
                  {["Tenor","Debt Service","DSCR Op","DSCR+Carbon*"].map(h=><th key={h} style={{textAlign:"left",padding:"4px 6px",fontSize:8,color:"#475569"}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {Object.values(fin.dscr_sensitivity||{}).map(row=>(
                    <tr key={row.tenor_yr} style={{borderBottom:"1px solid #0f172a",background:row.tenor_yr===tenor?"#111827":""}}>
                      <td style={{padding:"4px 6px",fontSize:9,fontWeight:700,color:row.tenor_yr===tenor?"#FFCB00":"#94a3b8"}}>{row.tenor_yr}yr{row.tenor_yr===tenor&&<span style={{fontSize:7,background:"#FFCB00",color:"#000",borderRadius:3,padding:"1px 3px",marginLeft:3}}>✓</span>}</td>
                      <td style={{padding:"4px 6px",fontSize:9,color:"#60a5fa"}}>{fmtU(row.annual_debt_service)}/yr</td>
                      <td style={{padding:"4px 6px",fontSize:9,fontWeight:700,color:row.dscr_op>=1.2?"#22c55e":"#f59e0b"}}>{row.dscr_op}x</td>
                      <td style={{padding:"4px 6px",fontSize:8,color:"#475569"}}>{row.dscr_with_carbon}x*</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{fontSize:7,color:"#475569",marginTop:6}}>*Carbon excluded from base DSCR per ICMA GBP 2021. Sensitivity only.</p>
            </div>
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>SLL KPIs</p>
              {sll.map(k=>(
                <div key={k.kpi} style={{marginBottom:8,borderBottom:"1px solid #0f172a",paddingBottom:6}}>
                  <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{k.kpi}</p>
                  <div style={{display:"flex",gap:8,marginTop:2}}>
                    <span style={{fontSize:8,color:"#475569"}}>Baseline: {k.baseline}</span>
                    <span style={{fontSize:8,color:"#22c55e"}}>Target 2030: {k.target_2030}</span>
                    <span style={{fontSize:8,color:"#FFCB00"}}>{k.margin_bps}bps margin</span>
                  </div>
                </div>
              ))}
            </div>
            {/* PDF Downloads */}
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Reports</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[["sustainable-finance","Sustainable Finance","ICMA GBP"],["executive","Executive Report","Board"]].map(([k,l,b])=>(
                  <button key={k} onClick={()=>downloadPdf(k)} disabled={downloading===k}
                    style={{display:"flex",alignItems:"center",gap:6,background:"#1d4ed8",color:"#fff",border:"none",borderRadius:6,padding:"6px 10px",fontSize:9,fontWeight:700,cursor:"pointer"}}
                    data-testid={`download-${k}`}>
                    {downloading===k?<Loader size={10} className="animate-spin"/>:<Download size={10}/>} {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DFI Readiness tab */}
      {activeTab==="dfi" && analysis && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="dfi-readiness">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>DFI Readiness Assessment</p>
            {[["ICMA GBP Framework",72,"Green Bond pre-issuance assessment required","#22c55e"],
              ["ESG Governance",58,"Board ESG committee active. TCFD alignment needed.","#f59e0b"],
              ["GHG Inventory Quality",55,"SSOT tracking active. VVB verification required for DFI submission.","#f59e0b"],
              ["Climate Risk Disclosure",45,"NGFS scenario analysis not yet commissioned. Critical gap.","#ef4444"],
              ["SBTi Alignment",30,"SBTi submission pending. DFIs increasingly require this.","#ef4444"],
              ["Transition Plan",40,"Tower solarisation programme qualifies as partial plan.","#ef4444"],
            ].map(([l,s,n,c])=>(
              <div key={l} style={{marginBottom:10,borderBottom:"1px solid #0f172a",paddingBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{l}</span>
                  <span style={{fontSize:10,fontWeight:900,color:c}}>{s}/100</span>
                </div>
                <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:3}}>
                  <div style={{height:"100%",width:`${s}%`,background:c,borderRadius:2}}/>
                </div>
                <p style={{fontSize:8,color:"#475569"}}>{n}</p>
              </div>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Blended Finance Optimization</p>
              {[["IFC / World Bank","Applicable","Tower solarisation + rural renewable","Climate co-finance, 3-5yr prep"],
                ["AfDB","Applicable","Energy efficiency + clean transport","Green Climate Fund access possible"],
                ["FMO","Conditional","Evidence quality upgrade required","T1 metering prerequisite"],
                ["PROPARCO","Conditional","TCFD disclosure required","Currently below threshold"],
                ["USAID DFC","Exploratory","Rural connectivity + climate crossover","Early engagement recommended"],
              ].map(([dfi,status,scope,note])=>(
                <div key={dfi} style={{marginBottom:6,borderBottom:"1px solid #0f172a",paddingBottom:5}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:1}}>
                    <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{dfi}</span>
                    <span style={{fontSize:8,fontWeight:700,padding:"1px 6px",borderRadius:3,background:status==="Applicable"?"#14532d30":"#78350f30",color:status==="Applicable"?"#22c55e":"#f59e0b"}}>{status}</span>
                  </div>
                  <p style={{fontSize:8,color:"#475569"}}>{scope}</p>
                  <p style={{fontSize:7,color:"#334155"}}>{note}</p>
                </div>
              ))}
            </div>
            <ExecutiveActionPanel compact={true} title="Financing Blockers" actions={[
              {severity:"High",category:"DFI Readiness",title:"TCFD disclosure gap blocks DFI access",description:"Multiple DFIs now require TCFD-aligned disclosure as prerequisite for climate finance.",actionRequired:"Commission TCFD gap analysis + climate scenario assessment",financialExposure:"$50M+ DFI access at risk",timeline:"Q2 2026",owner:"CFO + Sustainability",ifNothing:"DFI pipeline blocked. Cost of capital remains elevated."},
              {severity:"Medium",category:"Finance",title:"SBTi submission pending — increasing DFI prerequisite",description:"Science-Based Targets increasingly required by DFIs for green finance instruments.",actionRequired:"Submit SBTi targets by Q3 2026",financialExposure:"SLL margin + green bond credibility",timeline:"Q3 2026",owner:"Sustainability"},
            ]} />
          </div>
        </div>
      )}

      {/* Scenarios tab */}
      {activeTab==="scenarios" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid="scenarios-table">
          <div style={{padding:"10px 16px",borderBottom:"1px solid #1e293b"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>Transition Scenario Analysis — Nigerian Telecom Context</p>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#060c1a",borderBottom:"1px solid #1e293b"}}>
              {["Scenario","Emission Trajectory","Key Assumptions","CAPEX","Scope 1+2 Change","Risk"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"8px 12px",fontSize:9,color:"#475569",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[["BAU — No Transition","Growth-led emissions","No CAPEX, grid stays unreliable","$0","+8-15%","High regulatory/reputational"],
                ["Moderate (40% reduction)","Partial transition","Solar Phase 1 + battery opt","$30-40M","-25-35%","Medium — supply chain"],
                ["Accelerated (66% reduction)","Full solarisation","All tower solar + fleet CNG","$55-70M","-45-55%","High — CAPEX execution"],
                ["Net-Zero 2040 (Scope 1+2)","Near-zero operational","Full diesel exit + RE + 5G efficiency","$90M+","~-90% Scope 1+2","Very High — infrastructure"],
              ].map(([s,e,a,c,r,risk])=>(
                <tr key={s} style={{borderBottom:"1px solid #0f172a"}}>
                  <td style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{s}</td>
                  <td style={{padding:"8px 12px",fontSize:9,color:"#94a3b8"}}>{e}</td>
                  <td style={{padding:"8px 12px",fontSize:8,color:"#64748b"}}>{a}</td>
                  <td style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#60a5fa"}}>{c}</td>
                  <td style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#22c55e"}}>{r}</td>
                  <td style={{padding:"8px 12px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:risk.includes("High")?"#7f1d1d30":"#78350f30",color:risk.includes("High")?"#ef4444":"#f59e0b"}}>{risk.split("—")[0].trim()}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Projects tab */}
      {activeTab==="projects" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid="projects-ranking">
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#060c1a",borderBottom:"1px solid #1e293b"}}>
              {["#","Project","CAPEX","Reduction/yr","Payback","Readiness","Complexity","Bankability"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:9,color:"#475569",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {projList.map((p,i)=>{
                const bankable=p.readiness_score>=65&&p.icma_eligible;
                const needsWork=p.readiness_score>=40&&!bankable;
                return(
                  <tr key={p.project_id} style={{borderBottom:"1px solid #0f172a"}} data-testid={`finance-proj-${p.project_id}`}>
                    <td style={{padding:"7px 10px",fontSize:9,color:"#475569",fontWeight:700}}>#{i+1}</td>
                    <td style={{padding:"7px 10px"}}><p style={{fontSize:9,color:"#f1f5f9",fontWeight:600}}>{p.name?.substring(0,35)}</p><p style={{fontSize:8,color:"#475569"}}>{p.type}</p></td>
                    <td style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:"#60a5fa"}}>{fmtU(p.capex_usd)}</td>
                    <td style={{padding:"7px 10px",fontSize:9,color:"#22c55e",fontWeight:700}}>{fmtN(p.reduction_tco2e_yr,"tCO₂e")}</td>
                    <td style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{p.simple_payback_yrs}yr</td>
                    <td style={{padding:"7px 10px"}}><div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:36,height:4,background:"#1e293b",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${p.readiness_score}%`,background:"#22c55e"}}/></div><span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{p.readiness_score}</span></div></td>
                    <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:p.complexity==="Low"?"#14532d30":p.complexity==="Medium"?"#78350f30":"#7f1d1d30",color:p.complexity==="Low"?"#22c55e":p.complexity==="Medium"?"#f59e0b":"#ef4444"}}>{p.complexity}</span></td>
                    <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:bankable?"#14532d30":needsWork?"#78350f30":"#7f1d1d30",color:bankable?"#22c55e":needsWork?"#f59e0b":"#ef4444"}}>{bankable?"Bankable":needsWork?"Needs work":"Not ready"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
